//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";
// libraries
import * as React from "react";
import { createRefetchContainer } from "react-relay";
// team-settings-admin-team-calendar imports
import { TeamSettingsAdminTeamCalendar } from "./TeamSettingsAdminTeamCalendar";
import {
  refetchQuery,
  teamSettingsAdminTeamCalendarFragments,
} from "./TeamSettingsAdminTeamCalendar.gql";
import { IContainer, IProps, IState } from "./TeamSettingsAdminTeamCalendarTypes";
// other imports
// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { maybeGetTeamSettingsFromOrg } from "#webapp/src/util/team-settings.util";
// constants
// state
import { saveTeamSettings } from "#webapp/src/mutations";
import toast from "react-hot-toast";
// material-ui

import { sendM365TeamAvailabilityCalendarInvitesForTeam } from "#webapp/src/mutations";
import { EcosystemEnum } from "@clockwise/schema";
import { EcosystemContext } from "@clockwise/web-commons/src/util/ecosystem";
import { noop } from "lodash";
import { M365TeamSettingsAdminTeamCalendar } from "./M365TeamSettingsAdminTeamCalendar";

//////////////////
// COMPONENT
//////////////////
class TeamSettingsAdminTeamCalendarWrapperCmpt extends React.Component<IProps, IState> {
  static contextType = EcosystemContext;
  declare context: React.ContextType<typeof EcosystemContext>;

  constructor(props: IProps) {
    super(props);

    const teamSettings = maybeGetTeamSettingsFromOrg(props.org);

    this.state = {
      createOOOCalendar: teamSettings ? teamSettings.manageTeamCalendar : false,
      syncWFHEventsEnabled: teamSettings ? teamSettings.syncWFHEvents : false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private handleResendM365TeamAvailabilityCalendarInvite = () => {
    sendM365TeamAvailabilityCalendarInvitesForTeam(
      this.props.relay.environment,
      {
        teamId: this.props.team.teamId,
      },
      () => toast.success("Invites sent!"),
      () => toast.error("Failed to send invites"),
    );
  };

  private handleSharedCalendarToggle = () => {
    const { team } = this.props;
    const { createOOOCalendar } = this.state;
    const newSyncValue = !createOOOCalendar;

    this.setState({ createOOOCalendar: newSyncValue }, () => {
      // call save mutation
      const successCallback = () => {
        if (
          createOOOCalendar &&
          team.userSettings &&
          team.userSettings.__typename !== "GraphEntityError" &&
          team.userSettings.useTeamCalendar
        ) {
          PostMessageManager.toggleCalendar(team.teamCalendarId, true);
        }
        toast.success(`${newSyncValue ? "Created" : "Removed"} your team's calendar!`);

        // if creating a calendar in m365, call mutation to send email invites
        // to all the members of this team to join this calendar in outlook
        if (this.context === EcosystemEnum.Microsoft && newSyncValue) {
          sendM365TeamAvailabilityCalendarInvitesForTeam(
            this.props.relay.environment,
            {
              teamId: this.props.team.teamId,
            },
            noop,
            noop,
          );
        }
      };

      const failureCallback = () => {
        const msg = `Failed to ${newSyncValue ? "create" : "remove"} your team's calendar!`;
        toast.error(msg);
        logger.error(msg);
      };

      this.updateTeamSettings(successCallback, failureCallback);
    });

    track(TrackingEvents.TEAMS.TEAM_SETTINGS_ADMIN_TOGGLED_OOO_CALENDAR, {
      manageTeamCalendar: newSyncValue,
    });
  };

  private handleSyncWFHEventsCheck = () => {
    const { syncWFHEventsEnabled } = this.state;
    const newSyncValue = !syncWFHEventsEnabled;

    this.setState({ syncWFHEventsEnabled: newSyncValue }, () => {
      // call save mutation
      const successCallback = () => {
        toast.success(`Updated your team's calendar preferences!`);
      };

      const failureCallback = () => {
        const msg = `Failed to update your team's calendar preferences!`;
        toast.error(msg);
        logger.error(msg);
      };

      this.updateTeamSettings(successCallback, failureCallback);
    });

    track(TrackingEvents.TEAMS.TEAM_SETTINGS_ADMIN_TOGGLED_WFH_SYNC, {
      syncWFHEvents: newSyncValue,
    });
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private updateTeamSettings = (successCallback?: () => void, failureCallback?: () => void) => {
    const { relay, org, team } = this.props;
    const { createOOOCalendar, syncWFHEventsEnabled } = this.state;

    const input: ISchema.ISaveTeamSettingsMutationInput = {
      orgRelayId: org.id,
      teamId: team.teamId,
      manageTeamCalendar: createOOOCalendar,
      syncWFHEvents: syncWFHEventsEnabled,
    };

    saveTeamSettings(
      relay.environment,
      input,
      successCallback ? successCallback : () => null,
      failureCallback ? failureCallback : () => null,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  public render() {
    const { team } = this.props;
    const { createOOOCalendar, syncWFHEventsEnabled } = this.state;

    if (this.context === EcosystemEnum.Microsoft) {
      const teamSettings = maybeGetTeamSettingsFromOrg(this.props.org);
      const teamCalendarDetails =
        teamSettings?.m365TeamCalendarDetails?.__typename === "TeamCalendarDetails"
          ? teamSettings.m365TeamCalendarDetails
          : null;

      return (
        <M365TeamSettingsAdminTeamCalendar
          isTeamCalendarShareChecked={createOOOCalendar}
          isSyncWFHEventsEnabled={syncWFHEventsEnabled}
          teamName={team.teamName}
          onHandleSyncWFHEventsToggle={this.handleSyncWFHEventsCheck}
          onSharedCalendarToggle={this.handleSharedCalendarToggle}
          onResendInvites={this.handleResendM365TeamAvailabilityCalendarInvite}
          teamCalendarDetails={teamCalendarDetails}
        />
      );
    } else {
      return (
        <TeamSettingsAdminTeamCalendar
          isTeamCalendarShareChecked={createOOOCalendar}
          isSyncWFHEventsEnabled={syncWFHEventsEnabled}
          teamName={team.teamName}
          onHandleSyncWFHEventsToggle={this.handleSyncWFHEventsCheck}
          onSharedCalendarToggle={this.handleSharedCalendarToggle}
        />
      );
    }
  }
}

export const TeamSettingsAdminTeamCalendarWrapper = createRefetchContainer<IContainer>(
  TeamSettingsAdminTeamCalendarWrapperCmpt,
  teamSettingsAdminTeamCalendarFragments,
  refetchQuery,
);
